import React, { createContext, useContext, useState, useEffect } from "react";

export const REGIONS = {
    US: {
        code: "US",
        name: "United States",
        flag: "🇺🇸",
        currency: "USD",
        symbol: "$",
        locale: "en-US",
        taxName: "Sales Tax",
        defaultTaxRate: 8.25,
        taxOptions: [
            { label: "Standard Sales Tax (8.25%)", rate: 8.25 },
            { label: "State Tax (6.0%)", rate: 6.0 },
            { label: "Reduced Rate (4.0%)", rate: 4.0 },
            { label: "Tax Exempt (0%)", rate: 0.0 }
        ],
        pricing: {
            basic: { monthly: 12, annual: 10 },
            pro: { monthly: 24, annual: 20 },
            enterprise: "Custom"
        },
        phonePrefix: "+1",
        paymentBadges: ["Stripe", "Apple Pay", "Google Pay", "Visa/Mastercard", "ACH Transfer"],
        complianceBadge: "US GAAP & Multi-State Tax Ready"
    },
    CA: {
        code: "CA",
        name: "Canada",
        flag: "🇨🇦",
        currency: "CAD",
        symbol: "CA$",
        locale: "en-CA",
        taxName: "GST/HST",
        defaultTaxRate: 13,
        taxOptions: [
            { label: "HST Ontario (13%)", rate: 13.0 },
            { label: "GST + PST BC (12%)", rate: 12.0 },
            { label: "GST Federal Only (5%)", rate: 5.0 },
            { label: "Zero-Rated (0%)", rate: 0.0 }
        ],
        pricing: {
            basic: { monthly: 16, annual: 13 },
            pro: { monthly: 32, annual: 26 },
            enterprise: "Custom"
        },
        phonePrefix: "+1",
        paymentBadges: ["Stripe", "Apple Pay", "Interac", "Credit Cards"],
        complianceBadge: "CRA & GST/HST Ready"
    },
    GB: {
        code: "GB",
        name: "United Kingdom",
        flag: "🇬🇧",
        currency: "GBP",
        symbol: "£",
        locale: "en-GB",
        taxName: "VAT",
        defaultTaxRate: 20,
        taxOptions: [
            { label: "Standard VAT (20%)", rate: 20.0 },
            { label: "Reduced Rate (5%)", rate: 5.0 },
            { label: "Zero-Rated (0%)", rate: 0.0 },
            { label: "VAT Exempt (0%)", rate: 0.0 }
        ],
        pricing: {
            basic: { monthly: 9, annual: 7.5 },
            pro: { monthly: 19, annual: 15 },
            enterprise: "Custom"
        },
        phonePrefix: "+44",
        paymentBadges: ["Stripe", "Apple Pay", "BACS Direct Debit", "Visa/Mastercard"],
        complianceBadge: "HMRC Making Tax Digital (MTD)"
    },
    EU: {
        code: "EU",
        name: "European Union",
        flag: "🇪🇺",
        currency: "EUR",
        symbol: "€",
        locale: "de-DE",
        taxName: "VAT",
        defaultTaxRate: 20,
        taxOptions: [
            { label: "Standard VAT (19% - 21%)", rate: 20.0 },
            { label: "Reduced VAT (7% - 10%)", rate: 10.0 },
            { label: "Reverse Charge (0%)", rate: 0.0 },
            { label: "VAT Exempt (0%)", rate: 0.0 }
        ],
        pricing: {
            basic: { monthly: 11, annual: 9 },
            pro: { monthly: 22, annual: 18 },
            enterprise: "Custom"
        },
        phonePrefix: "+49",
        paymentBadges: ["SEPA Direct Debit", "Stripe", "Apple Pay", "iDEAL", "Cards"],
        complianceBadge: "EU VAT OSS & GDPR Compliant"
    },
    AE: {
        code: "AE",
        name: "UAE & Middle East",
        flag: "🇦🇪",
        currency: "AED",
        symbol: "AED ",
        locale: "en-AE",
        taxName: "VAT",
        defaultTaxRate: 5,
        taxOptions: [
            { label: "Standard UAE VAT (5%)", rate: 5.0 },
            { label: "Designated Zone (0%)", rate: 0.0 },
            { label: "Export / Zero-Rated (0%)", rate: 0.0 },
            { label: "Exempt (0%)", rate: 0.0 }
        ],
        pricing: {
            basic: { monthly: 45, annual: 37 },
            pro: { monthly: 89, annual: 72 },
            enterprise: "Custom"
        },
        phonePrefix: "+971",
        paymentBadges: ["Apple Pay", "Stripe", "Tabby", "Credit/Debit Cards", "WhatsApp Pay"],
        complianceBadge: "FTA VAT & E-Invoicing Compliant"
    },
    IN: {
        code: "IN",
        name: "India",
        flag: "🇮🇳",
        currency: "INR",
        symbol: "₹",
        locale: "en-IN",
        taxName: "GST",
        defaultTaxRate: 18,
        taxOptions: [
            { label: "Standard GST (18%)", rate: 18.0 },
            { label: "Reduced GST (12%)", rate: 12.0 },
            { label: "Essential Goods (5%)", rate: 5.0 },
            { label: "Nil Rated / Exempt (0%)", rate: 0.0 }
        ],
        pricing: {
            basic: { monthly: 550, annual: 500 },
            pro: { monthly: 750, annual: 700 },
            enterprise: "Custom"
        },
        phonePrefix: "+91",
        paymentBadges: ["UPI (GPay, PhonePe, Paytm)", "Cards", "Net Banking", "Razorpay"],
        complianceBadge: "GST & E-Way Bill Compliant"
    }
};

const RegionContext = createContext(null);

export function RegionProvider({ children }) {
    const [regionCode, setRegionCode] = useState(() => {
        const saved = localStorage.getItem("user_region");
        if (saved && REGIONS[saved]) return saved;
        // Default to US so all visitors see Dollar ($) and Sales Tax by default
        return "US";
    });

    useEffect(() => {
        localStorage.setItem("user_region", regionCode);
    }, [regionCode]);

    const region = REGIONS[regionCode] || REGIONS.US;

    const formatCurrency = (amount, overrideCurrency = null) => {
        const targetRegion = overrideCurrency ? (REGIONS[overrideCurrency] || region) : region;
        const num = Number(amount) || 0;
        return `${targetRegion.symbol}${num.toLocaleString(targetRegion.locale, {
            minimumFractionDigits: num % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2
        })}`;
    };

    return (
        <RegionContext.Provider
            value={{
                region,
                regionCode,
                setRegion: setRegionCode,
                allRegions: REGIONS,
                formatCurrency
            }}
        >
            {children}
        </RegionContext.Provider>
    );
}

export function useRegion() {
    const context = useContext(RegionContext);
    if (!context) {
        throw new Error("useRegion must be used within a RegionProvider");
    }
    return context;
}
